import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PlusIcon } from 'lucide-react';

import { SectionContainer } from 'components/DetailsView/Section';
import { FileDragAndDrop, FileWithPreview } from 'components/FileDragAndDrop';
import { TotemImage } from 'components/TotemImage';
import { TotemTrashButton } from 'components/TotemTrashButton';
import { Button } from 'components/ui/button';

import { computeThumbhash } from 'helpers/thumbhash';

import type { MealPlanDefinitionFormData, MealPlanImageField } from '../constants/formData';

const IMAGE_TYPES = ['image/jpeg', 'image/png'];

const EMPTY_IMAGE: MealPlanImageField = { url: null, file: null, preview: null, thumbhash: '' };

/**
 * Les visuels de la formule, affichés en tuiles dans l'application mobile.
 *
 * Chaque image porte un `thumbhash` : l'aperçu flouté montré le temps du chargement. Il est calculé
 * ICI, au dépôt du fichier, parce que le navigateur sait décoder une image et que l'API, elle, ne le
 * peut pas — son image de production ne contient que le bundle, sans bibliothèque native.
 */
export const ImagesSection = () => {
    const { setValue, watch } = useFormContext<MealPlanDefinitionFormData>();
    const { fields, append, remove } = useFieldArray<MealPlanDefinitionFormData>({ name: 'images' });
    const [pendingIndex, setPendingIndex] = useState<number | null>(null);

    const images = watch('images');

    const onFileDrop = async (index: number, droppedFiles: FileWithPreview[] | null) => {
        const file = droppedFiles?.[0];
        if (!file) {
            return;
        }

        setPendingIndex(index);

        try {
            const thumbhash = await computeThumbhash(file);

            // `setValue` plutôt que `update` : `update` de react-hook-form démonte puis remonte la
            // ligne, ce qui referait perdre le focus et clignoter l'aperçu.
            setValue(`images.${index}`, { url: null, file, preview: file.preview, thumbhash }, { shouldDirty: true });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Impossible de lire l'image");
        } finally {
            setPendingIndex(null);
        }
    };

    return (
        <SectionContainer title="Visuels" isInitiallyOpen>
            <div className="flex flex-col gap-3">
                <span className="text-[13px] text-muted-foreground">
                    Déposez une image JPEG ou PNG : son aperçu flouté (thumbhash) est calculé automatiquement, et le
                    fichier est téléversé à l'enregistrement.
                </span>
                <div className="flex flex-wrap gap-4">
                    {fields.map((field, index) => {
                        const image = images?.[index];

                        return (
                            <div
                                key={field.id}
                                className="flex flex-col items-center gap-2 rounded-lg border border-border p-3"
                            >
                                <FileDragAndDrop
                                    multiple={false}
                                    hasPadding={false}
                                    fileTypes={IMAGE_TYPES}
                                    onFileDropCallback={(droppedFiles) => onFileDrop(index, droppedFiles)}
                                >
                                    <div className="cursor-pointer hover:opacity-40">
                                        <TotemImage
                                            src={image?.preview || image?.url || null}
                                            alt="Visuel"
                                            size={160}
                                        />
                                    </div>
                                </FileDragAndDrop>
                                <span className="text-[13px] text-muted-foreground">
                                    {pendingIndex === index
                                        ? 'Calcul de l’aperçu…'
                                        : image?.thumbhash
                                          ? 'Aperçu prêt'
                                          : 'Aucune image'}
                                </span>
                                <TotemTrashButton onClick={() => remove(index)} />
                            </div>
                        );
                    })}
                </div>
                <div>
                    <Button
                        type="button"
                        size="icon-sm"
                        onClick={() => append(EMPTY_IMAGE)}
                        data-test="meal-plan-definition-add-image"
                    >
                        <PlusIcon />
                    </Button>
                </div>
            </div>
        </SectionContainer>
    );
};
